import React from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const STATUS_COLORS = ['#9CA3AF', '#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#22C55E'];

const OrderPipeline = ({ data }) => {
    if (!data || data.length === 0) return null;

    const labels = data.map(d => `${d.label} (${d.count})`);
    const values = data.map(d => d.count);
    const colors = data.map((_, i) => STATUS_COLORS[i % STATUS_COLORS.length]);

    const chartData = {
        labels,
        datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        indexAxis: 'y',
        plugins: {
            legend: { display: false },
        },
        scales: {
            x: { title: { display: true, text: 'Orders' } },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Order Status Pipeline</h6>
                <Bar data={chartData} options={options} />
            </div>
        </div>
    );
};

const OrderTypeBreakdown = ({ data }) => {
    if (!data || data.length === 0) return null;

    const labels = data.map(d => d.label);
    const values = data.map(d => d.count);
    const colors = ['#3B82F6', '#F59E0B'];

    const chartData = {
        labels,
        datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8 } },
        },
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Order Types</h6>
                <Doughnut data={chartData} options={options} />
            </div>
        </div>
    );
};

const DeliveryPerformance = ({ data }) => {
    if (!data || !data.total_orders) return null;

    const labels = ['Delivered', 'Pending'];
    const values = [data.delivered || 0, data.pending || 0];
    const colors = ['#10B981', '#F59E0B'];

    const chartData = {
        labels,
        datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
        }],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8 } },
        },
        cutout: '60%',
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h6 className="card-title mb-3">Delivery Performance</h6>
                <div className="row align-items-center">
                    <div className="col-md-5">
                        <Doughnut data={chartData} options={options} />
                    </div>
                    <div className="col-md-7">
                        <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between border-bottom pb-1">
                                <span>Total Orders</span>
                                <strong>{data.total_orders}</strong>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-1 text-success">
                                <span>Delivered</span>
                                <strong>{data.delivered}</strong>
                            </div>
                            <div className="d-flex justify-content-between border-bottom pb-1 text-warning">
                                <span>Pending</span>
                                <strong>{data.pending}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="fw-bold">Delivery Rate</span>
                                <span className={`fw-bold ${data.delivery_rate >= 70 ? 'text-success' : 'text-danger'}`}>
                                    {data.delivery_rate}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const OrderCharts = ({ orderPipeline, orderTypeBreakdown, deliveryPerformance }) => {
    if (!orderPipeline && !orderTypeBreakdown && !deliveryPerformance) {
        return null;
    }

    return (
        <div className="mb-4">
            <h5 className="mb-3 border-bottom pb-2">Order Pipeline & Delivery</h5>
            <div className="row g-3">
                <div className="col-md-5">
                    <OrderPipeline data={orderPipeline} />
                </div>
                <div className="col-md-3">
                    <OrderTypeBreakdown data={orderTypeBreakdown} />
                </div>
                <div className="col-md-4">
                    <DeliveryPerformance data={deliveryPerformance} />
                </div>
            </div>
        </div>
    );
};

export default OrderCharts;
